import styled from "@emotion/styled"
import { PButtonPure, PTable, PTableBody, PTableCell, PText } from "@porsche-design-system/components-react"
import { spacingFluidMd } from "@porsche-design-system/components-react/emotion"
import type { NewsListItem } from "@slfinrtl/ui-bff-routes"
import { FormattedMessage, useIntl } from "react-intl"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import { TableRowLink, TableRowWithLink } from "src/components"
import { NewsListTableHead } from "./NewsListTableHead"
import { NewsMarketplaces } from "./NewsMarketplaces"
import { NewsReviewProgress } from "./NewsReviewProgress"
import { NewsStatusTag } from "./NewsStatusTag"
import { newsListMessages as messages } from "./newsList.messages"

interface NewsListProps {
	getItemUrl: (item: NewsListItem) => string
	items: NewsListItem[]
	/** the delete column is hidden if undefined */
	onDelete?: (item: NewsListItem) => void
}

export const NewsList = ({ getItemUrl, items, onDelete }: NewsListProps) => {
	const { formatDate, formatMessage } = useIntl()

	if (items.length === 0) {
		return (
			<NoEntries>
				<PText align="center">
					<FormattedMessage {...messages.noEntries} />
				</PText>
			</NoEntries>
		)
	}

	return (
		<PTable caption={formatMessage(messages.listCaption)} sticky>
			<NewsListTableHead hasActionColumn={onDelete !== undefined} />
			<PTableBody>
				{items.map((item) => (
					<TableRowWithLink key={item.id}>
						<PTableCell>
							<PText>
								<TableRowLink to={getItemUrl(item)}>{item.title}</TableRowLink>
							</PText>
						</PTableCell>
						<PTableCell>
							<NewsStatusTag status={item.status} />
						</PTableCell>
						<PTableCell>
							{item.reviewProgress ? (
								<NewsReviewProgress {...item.reviewProgress} />
							) : (
								<PText color="inherit">{emptyPlaceholder}</PText>
							)}
						</PTableCell>
						<PTableCell>
							<NewsMarketplaces marketplaceKeys={item.marketplaceKeys} />
						</PTableCell>
						<PTableCell>
							<PText color="inherit" ellipsis>
								{formatDate(item.updatedAt)}
							</PText>
							<PText color="contrast-medium" size="sm" ellipsis>
								{item.updatedBy}
							</PText>
						</PTableCell>
						{onDelete && (
							<PTableCell>
								<DeleteButton type="button" hideLabel icon="delete" onClick={() => onDelete(item)}>
									<FormattedMessage {...messages.deleteEntry} />
								</DeleteButton>
							</PTableCell>
						)}
					</TableRowWithLink>
				))}
			</PTableBody>
		</PTable>
	)
}

const NoEntries = styled.div`
	padding-block: ${spacingFluidMd};
`

// keeps the delete button clickable above the row link overlay
const DeleteButton = styled(PButtonPure)`
	position: relative;
`
